import { useMemo } from 'react'

import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { fetchQuery, useQuery } from '@sequencehq/api'
import { lineItemGroupsToBreakdownRows } from '@sequencehq/invoice-content'
import { toMoney } from '@sequencehq/utils'
import { formatDateRange } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'

export const prefetchInvoiceData = ({
  invoiceId,
  customerId
}: {
  invoiceId: string
  customerId?: string
}) => {
  return Promise.all([
    fetchQuery(apiQueryClient, dashboardv99990101Client.getInvoice, {
      id: invoiceId ?? ''
    }),
    customerId
      ? fetchQuery(apiQueryClient, dashboardv99990101Client.getCustomer, {
          id: customerId
        })
      : Promise.resolve(),
    fetchQuery(apiQueryClient, dashboardv99990101Client.getAllLineItems, {
      invoiceId: invoiceId ?? ''
    }),
    fetchQuery(apiQueryClient, dashboardv99990101Client.getAllLineItemGroups, {
      invoiceId: invoiceId ?? ''
    })
  ])
}

export const useInvoiceData = (invoiceId: string | undefined) => {
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId ?? '' },
    { enabled: !!invoiceId }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: invoiceQuery.data?.customerId ?? '' },
    { enabled: !!invoiceQuery.data?.customerId }
  )

  const lineItemsQuery = useQuery(
    dashboardv99990101Client.getAllLineItems,
    { invoiceId: invoiceId ?? '' },
    { enabled: !!invoiceId }
  )

  const lineItemGroupsQuery = useQuery(
    dashboardv99990101Client.getAllLineItemGroups,
    { invoiceId: invoiceId ?? '' },
    { enabled: !!invoiceId }
  )

  const invoice = invoiceQuery.data

  const invoiceDetails = useMemo(() => {
    const lineItems = lineItemsQuery.data?.items
    const lineItemGroups = lineItemGroupsQuery.data?.items

    if (!invoice || !lineItems || !lineItemGroups) {
      return undefined
    }

    const aggregatedLineItemGroups = lineItemGroups.map(group => ({
      ...group,
      invoiceId: invoice.id,
      lineItems: lineItems
        .filter(item => item.groupId === group.id)
        .map(item => ({
          ...item,
          invoiceId: invoice.id,
          groupId: group.id,
          externalIds: [],
          servicePeriod: {
            startDate:
              item.servicePeriodStart ?? item.servicePeriod?.startDate ?? '',
            endDate: item.servicePeriodEnd ?? item.servicePeriod?.endDate ?? ''
          }
        }))
    }))

    const breakdownRows = lineItemGroupsToBreakdownRows({
      lineItemGroups: aggregatedLineItemGroups,
      currency: invoice.currency
    })

    const billingPeriod = invoice.billingPeriod
      ? formatDateRange({
          from: new Date(invoice.billingPeriod.start),
          to: new Date(invoice.billingPeriod.endInclusive)
        })
      : undefined

    const groupSummary = lineItemGroups.map(group => ({
      description: group.title || group.description || `Group ${group.id}`,
      total: toMoney({
        value: group.netTotal,
        currency: invoice.currency
      })
    }))

    return {
      billingPeriod,
      groupSummary,
      totals: {
        currency: invoice.currency,
        netTotal: invoice.netTotal,
        grossTotal: invoice.grossTotal,
        breakdownRows
      }
    }
  }, [invoice, lineItemsQuery.data, lineItemGroupsQuery.data])

  return {
    invoice,
    customer: customerQuery.data,
    isPending:
      invoiceQuery.isPending ||
      customerQuery.isPending ||
      lineItemsQuery.isPending ||
      lineItemGroupsQuery.isPending,
    invoiceDetails
  }
}
